/**
 * Migration Helper Module
 *
 * Provides utilities to migrate from old data structures to the new
 * unified CompositionState structure.
 *
 * Handles backward compatibility between:
 * - Old interactiveMelody format → New CompositionState
 * - Old progressionData format → New CompositionState
 */

import { getCompositionState } from '../state/compositionState.js';
import { getProgressionData, getCurrentKey } from '../state/trainerState.js';

/**
 * Migrate from old interactiveMelody to CompositionState
 * @param {object} interactiveMelody - Old melody format
 * @param {object} progressionData - Old progression format
 * @returns {object} CompositionState instance
 */
export function migrateToCompositionState(interactiveMelody, progressionData) {
    const composition = getCompositionState();
    composition.clear();

    // Set metadata from melody
    if (interactiveMelody) {
        composition.metadata.tempo = interactiveMelody.tempo || 120;
        composition.metadata.key = interactiveMelody.key || 'C';

        if (interactiveMelody.timeSignature) {
            const [num, denom] = interactiveMelody.timeSignature.split('/').map(Number);
            composition.metadata.timeSignature = { num, denom };
        }
    }

    // Determine number of measures needed
    let maxMeasure = 0;

    // Check melody notes for max measure
    if (interactiveMelody && interactiveMelody.melodyNotes) {
        interactiveMelody.melodyNotes.forEach(note => {
            if (note.measure > maxMeasure) maxMeasure = note.measure;
        });
    }

    // Check chord notes for max measure
    if (interactiveMelody && interactiveMelody.chordNotes) {
        interactiveMelody.chordNotes.forEach(note => {
            if (note.measure > maxMeasure) maxMeasure = note.measure;
        });
    }

    // Check progression data
    if (progressionData && progressionData.length > 0) {
        maxMeasure = Math.max(maxMeasure, progressionData.length - 1);
    }

    // Create measures
    for (let measureIndex = 0; measureIndex <= maxMeasure; measureIndex++) {
        // Get chord data for this measure (if available)
        const chordData = progressionData && progressionData[measureIndex] ? {
            root: progressionData[measureIndex].root,
            type: progressionData[measureIndex].type,
            inversion: progressionData[measureIndex].inversion || 0,
            voicing: "close",
            roman: progressionData[measureIndex].roman,
            name: progressionData[measureIndex].name || progressionData[measureIndex].simpleName,
            notes: progressionData[measureIndex].notes || []
        } : null;

        // Add measure
        composition.addMeasure({ chord: chordData });
        const measure = composition.getMeasure(measureIndex);

        // Add melody notes to treble staff
        if (interactiveMelody && interactiveMelody.melodyNotes) {
            const melodyNotesForMeasure = interactiveMelody.melodyNotes.filter(
                note => note.measure === measureIndex
            );

            measure.notation.treble.voices[0].notes = melodyNotesForMeasure.map(note => ({
                type: note.type || 'note',
                pitch: note.pitch,
                duration: note.duration,
                beat: note.beat || 0,
                dotted: note.dotted || false,
                tied: note.tied || false,
                accidental: note.accidental || null,
                dynamic: note.dynamic || null
            }));
        }

        // Add chord notes to bass staff
        if (interactiveMelody && interactiveMelody.chordNotes) {
            const bassNotesForMeasure = interactiveMelody.chordNotes.filter(
                note => note.measure === measureIndex
            );

            if (bassNotesForMeasure.length > 0) {
                measure.notation.bass.voices[0].notes = bassNotesForMeasure.map(note => ({
                    type: note.type || 'note',
                    pitch: note.pitch || (note.pitches ? note.pitches[0] : null),
                    pitches: note.pitches || [note.pitch],
                    duration: note.duration,
                    beat: note.beat || 0,
                    dotted: note.dotted || false
                }));
                measure.notation.bass.autoGenerated = false; // User had notes
            } else if (chordData) {
                // Auto-generate bass if we have chord but no bass notes
                composition.updateBassFromChord(measureIndex);
            }
        } else if (chordData) {
            // No bass notes in old format, auto-generate
            composition.updateBassFromChord(measureIndex);
        }
    }

    return composition;
}

/**
 * Migrate only from progressionData to CompositionState
 * @param {array} progressionData - Old progression format
 * @param {string} key - Current key
 * @returns {object} CompositionState instance
 */
export function migrateProgressionOnly(progressionData, key) {
    const composition = getCompositionState();
    composition.clear();

    composition.metadata.key = key || 'C';

    // Import progression data
    composition.importFromProgressionData(progressionData, { key });

    return composition;
}

/**
 * Auto-migrate when switching to composition tab
 * Detects old format and converts automatically
 */
export function autoMigrateOnTabSwitch() {
    const progressionData = getProgressionData();
    const currentKey = getCurrentKey();

    if (!progressionData || progressionData.length === 0) {
        return null;
    }

    // Check if we have old interactiveMelody data in localStorage
    const storedMelody = localStorage.getItem('interactiveMelody');
    let interactiveMelody = null;

    if (storedMelody) {
        try {
            interactiveMelody = JSON.parse(storedMelody);
        } catch (error) {
            console.warn('[Migration] Failed to parse stored melody:', error);
        }
    }

    // Check if we have interactiveMelody in window scope (from melodyGenerator.js)
    if (!interactiveMelody && window.getInteractiveMelody) {
        interactiveMelody = window.getInteractiveMelody();
    }

    // Perform migration
    if (interactiveMelody && interactiveMelody.melodyNotes && interactiveMelody.melodyNotes.length > 0) {
        return migrateToCompositionState(interactiveMelody, progressionData);
    } else {
        return migrateProgressionOnly(progressionData, currentKey);
    }
}

/**
 * Export current CompositionState back to old formats
 * Useful for backward compatibility with old code
 * @param {object} composition - CompositionState instance
 * @returns {object} { interactiveMelody, progressionData }
 */
export function exportToOldFormats(composition) {
    const interactiveMelody = composition.exportToInteractiveMelody();
    const progressionData = composition.exportToProgressionData();

    return {
        interactiveMelody,
        progressionData
    };
}

/**
 * Check if migration is needed
 * @returns {boolean} True if old data exists and needs migration
 */
export function needsMigration() {
    const progressionData = getProgressionData();

    // Check if composition state is empty but progression data exists
    const composition = getCompositionState();

    if (!progressionData || progressionData.length === 0) {
        return false; // Nothing to migrate
    }

    if (composition.getMeasureCount() === 0) {
        return true; // Need to migrate
    }

    // Check if composition is out of sync with progression
    if (composition.getMeasureCount() !== progressionData.length) {
        return true;
    }

    return false;
}

/**
 * Validate migrated data
 * @param {object} composition - CompositionState instance
 * @returns {object} Validation result { valid: boolean, errors: [] }
 */
export function validateMigration(composition) {
    const errors = [];

    if (!composition) {
        return { valid: false, errors: ['Composition is null'] };
    }

    if (composition.getMeasureCount() === 0) {
        errors.push('No measures found after migration');
    }

    // Check each measure
    composition.measures.forEach((measure, idx) => {
        if (!measure.chord) {
            errors.push(`Measure ${idx} has no chord data`);
        }

        if (!measure.notation) {
            errors.push(`Measure ${idx} has no notation data`);
        }

        if (!measure.notation.treble || !measure.notation.bass) {
            errors.push(`Measure ${idx} missing treble or bass staff`);
        }
    });

    return {
        valid: errors.length === 0,
        errors
    };
}

/**
 * Backup old data before migration
 * Stores in localStorage with timestamp
 */
export function backupOldData() {
    const progressionData = getProgressionData();
    const timestamp = new Date().toISOString();

    // Get old interactive melody if available
    let interactiveMelody = null;
    if (window.getInteractiveMelody) {
        interactiveMelody = window.getInteractiveMelody();
    }

    const backup = {
        timestamp,
        progressionData,
        interactiveMelody
    };

    try {
        localStorage.setItem('compositionBackup', JSON.stringify(backup));
        return true;
    } catch (error) {
        console.error('[Migration] Failed to create backup:', error);
        return false;
    }
}

/**
 * Restore from backup
 * @returns {object|null} Restored data or null if no backup
 */
export function restoreFromBackup() {
    try {
        const backupData = localStorage.getItem('compositionBackup');
        if (!backupData) {
            return null;
        }

        const backup = JSON.parse(backupData);
        return backup;
    } catch (error) {
        console.error('[Migration] Failed to restore backup:', error);
        return null;
    }
}
